import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowUpRight, MapPin, Target, Sparkles, HeartHandshake, X } from "lucide-react";
import { Navbar } from "@/components/site/Navbar";
import { Footer } from "@/components/site/Footer";
import { BRAND, STATS } from "@/data/content";

const ABOUT_VIDEO = "/assets/about-story.mp4";

const HERO_GALLERY = [
  { src: "/assets/hero-1.png", caption: "Can't figure out the mess your webmaster left you?" },
  { src: "/assets/hero-2-captain.png", caption: "Have a website that needs saving?" },
  { src: "/assets/hero-3-csw.png", caption: "Captain Save A Website — to the rescue." },
  { src: "/assets/hero-4-cooldaddy.png", caption: "Cool Daddy, live from the cosmos." },
  { src: "/assets/hero-5-convergence.png", caption: "A convergence of heroes vs. 'The Brand'." },
];

const FOUNDERS = [
  {
    name: "Nicholas Fordham",
    alias: "Captain Save A Website",
    role: "The Web Guru · Founder",
    img: "/assets/hero-2-captain.png",
    bio: "A veteran of web design and marketing affectionately known as the \"Web Guru,\" Nicholas launched his first web development firm in 1998. With 25+ years in the trenches, he's rescued, rebuilt, and ranked countless sites — and he's the technical force behind every project at Emergent Sacramento.",
  },
  {
    name: "Charles Gwyn",
    alias: "Cool Daddy",
    role: "Brand & Media · Partner",
    img: "/assets/hero-4-cooldaddy.png",
    bio: "With over 30 years in the music industry, Charles \"Cool Daddy\" Gwyn brings unmatched brand, culture, and media firepower. He knows how to make an audience feel something — translating that instinct into campaigns and content that get Sacramento brands seen and heard.",
  },
];

const VALUES = [
  { icon: Target, title: "Results over vanity", text: "We measure success in leads, revenue, and growth — not likes for the sake of likes." },
  { icon: MapPin, title: "Local first", text: "Sacramento is home. We build for the businesses, neighborhoods, and people we know." },
  { icon: Sparkles, title: "Craft & clarity", text: "Bold design with a purpose. Every pixel and headline earns its place." },
  { icon: HeartHandshake, title: "True partnership", text: "We're an extension of your team — responsive, transparent, and invested in your win." },
];

const STUDIO = [
  { src: "/assets/studio-greenscreen.jpg", label: "Green-screen video production" },
  { src: "/assets/studio-stream.jpg", label: "Live concert streaming & switching" },
  { src: "/assets/studio-podcast.jpg", label: "In-house podcast & interview set" },
  { src: "/assets/studio-gear.jpg", label: "Pro audio & recording rig" },
  { src: "/assets/studio-booth.jpg", label: "Vocal recording booth" },
  { src: "/assets/studio-eventhall.jpg", label: "Event & live-event space" },
];

export default function About() {
  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = "About Us | Sacramento Web Design & Marketing Since 1998 | Emergent Sacramento";
  }, []);

  const [lightbox, setLightbox] = useState(null);

  return (
    <div className="bg-ink text-white font-body min-h-screen">
      <Navbar />

      <main>
        {/* Hero */}
        <section data-testid="about-hero" className="relative pt-36 pb-20 border-b border-line">
          <div className="container-x">
            <motion.span
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="mono-label text-accent"
            >
              [ About Us · Est. 1998 ]
            </motion.span>
            <motion.h1
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="font-head font-black tracking-tighter text-5xl sm:text-6xl lg:text-7xl mt-5 max-w-4xl text-balance leading-[0.95]"
            >
              25+ years building the brands of Sacramento.
            </motion.h1>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.25 }}
              className="mt-7 text-lg text-text-2 leading-relaxed max-w-2xl"
            >
              We're {BRAND.name} — founded in 1998 and now your official Emergent Technologies
              partner for the 916. Web design, branding, SEO, and social campaigns under one roof,
              engineered to turn local attention into real revenue.
            </motion.p>
          </div>
        </section>

        {/* Video */}
        <section data-testid="about-video-section" className="relative py-20 border-b border-line">
          <div className="container-x">
            <motion.div
              initial={{ opacity: 0, scale: 0.97 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="relative border border-line bg-black overflow-hidden"
            >
              <video
                data-testid="about-video"
                src={ABOUT_VIDEO}
                controls
                autoPlay
                muted
                loop
                playsInline
                className="w-full max-h-[70vh] object-contain bg-black"
              />
              <div className="absolute -top-4 -left-4 w-20 h-20 border-t-2 border-l-2 border-accent pointer-events-none" />
              <div className="absolute -bottom-4 -right-4 w-20 h-20 border-b-2 border-r-2 border-accent pointer-events-none" />
            </motion.div>
            <p className="mono-label text-text-2 mt-5 text-center">Our story, in motion.</p>
          </div>
        </section>

        {/* Featured YouTube video */}
        <section data-testid="about-yt-section" className="relative py-20 border-b border-line">
          <div className="container-x">
            <div className="max-w-2xl mb-10">
              <span className="mono-label text-accent">[ Featured · Proof in the work ]</span>
              <h2 className="font-head font-black tracking-tighter text-4xl sm:text-5xl mt-4 text-balance">
                We've been doing this for years.
              </h2>
              <p className="text-text-2 mt-4 leading-relaxed">
                This isn't a side hustle or a trend we jumped on. From production to promotion,
                we've been creating, filming, and marketing for Sacramento long before it was easy.
              </p>
            </div>
            <motion.div
              initial={{ opacity: 0, scale: 0.97 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="relative border border-line overflow-hidden bg-black aspect-video"
            >
              <iframe
                data-testid="about-youtube"
                src="https://www.youtube-nocookie.com/embed/XnEgjJ1oVVc"
                title="Emergent Sacramento — Featured video"
                className="absolute inset-0 w-full h-full"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                referrerPolicy="strict-origin-when-cross-origin"
                allowFullScreen
              />
            </motion.div>
          </div>
        </section>


        {/* Behind the scenes — real studio photos (replaces superhero gallery) */}
        <section data-testid="about-gallery" className="relative py-24 border-b border-line">
          <div className="container-x">
            <div className="max-w-2xl mb-14">
              <span className="mono-label text-accent">[ Behind the scenes ]</span>
              <h2 className="font-head font-black tracking-tighter text-4xl sm:text-5xl mt-4 text-balance">
                Our studio & production space.
              </h2>
              <p className="text-text-2 mt-4 leading-relaxed">
                Video, audio, podcasts, live streaming, and events — produced in-house, right here in
                Sacramento. Tap any photo to enlarge.
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {STUDIO.map((s, i) => (
                <motion.button
                  key={s.src}
                  type="button"
                  onClick={() => setLightbox({ src: s.src, caption: s.label })}
                  data-testid={`gallery-img-${i}`}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.45, delay: i * 0.06 }}
                  className="group relative overflow-hidden border border-line accent-glow text-left"
                >
                  <div className="aspect-[4/3] overflow-hidden">
                    <img src={s.src} alt={s.label} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                  </div>
                  <div className="absolute inset-0 bg-gradient-to-t from-ink/90 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
                  <div className="absolute bottom-0 left-0 right-0 p-4 translate-y-2 group-hover:translate-y-0 opacity-0 group-hover:opacity-100 transition-all">
                    <p className="mono-label text-white !text-[0.6rem]">{s.label}</p>
                  </div>
                </motion.button>
              ))}
            </div>
          </div>
        </section>

        {/* Founders */}
        <section data-testid="about-founders" className="relative py-24 border-b border-line">
          <div className="container-x">
            <div className="max-w-2xl mb-14">
              <span className="mono-label text-accent">[ The team behind the capes ]</span>
              <h2 className="font-head font-black tracking-tighter text-4xl sm:text-5xl mt-4 text-balance">
                Meet your founders.
              </h2>
              <p className="text-text-2 mt-4 leading-relaxed">
                Two veterans, one mission: make Sacramento businesses impossible to ignore.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {FOUNDERS.map((f, i) => (
                <motion.div
                  key={f.name}
                  initial={{ opacity: 0, y: 24 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: i * 0.1 }}
                  data-testid={`founder-${i}`}
                  className="group border border-line bg-surface overflow-hidden flex flex-col sm:flex-row accent-glow"
                >
                  <div className="relative sm:w-2/5 shrink-0 overflow-hidden bg-surface-2">
                    <img
                      src={f.img}
                      alt={`${f.name} as ${f.alias}`}
                      loading="lazy"
                      className="w-full h-64 sm:h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                  <div className="p-7 lg:p-8 flex flex-col">
                    <span className="mono-label text-accent">{f.alias}</span>
                    <h3 className="font-head font-black text-2xl sm:text-3xl mt-2">{f.name}</h3>
                    <div className="mono-label text-text-2 mt-1 !tracking-normal !text-[0.65rem]">{f.role}</div>
                    <p className="text-text-2 leading-relaxed mt-4 text-sm">{f.bio}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Story */}
        <section className="relative py-24 border-b border-line">
          <div className="container-x grid grid-cols-1 lg:grid-cols-12 gap-12">
            <motion.div
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="lg:col-span-5"
            >
              <span className="mono-label text-accent">[ Our story ]</span>
              <h2 className="font-head font-black tracking-tighter text-4xl sm:text-5xl mt-4 text-balance">
                Big-agency work,<br />neighbor-level care.
              </h2>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="lg:col-span-6 lg:col-start-7 space-y-5 text-text-2 text-lg leading-relaxed"
            >
              <p>
                We launched our web development firm in <span className="text-white font-semibold">1998</span> —
                back when "getting online" meant hand-coded HTML and dial-up. More than two decades
                later, we've evolved with every wave of technology while keeping one belief constant:
                Sacramento businesses deserve the same caliber of design and marketing as the brands
                you see in Silicon Valley — without the cold, faceless agency experience.
              </p>
              <p>
                With <span className="text-white font-semibold">25+ years</span> of hands-on experience
                and our standing as an official Emergent Technologies partner, we pair cutting-edge tools
                with seasoned local strategy. From a brand-new storefront in Midtown to an established
                firm scaling across the region, we build the websites, identities, and campaigns that get
                our neighbors noticed and chosen.
              </p>
              <p className="text-white">
                A quarter-century in, the mission is unchanged: when you win, we win.
              </p>
            </motion.div>
          </div>
        </section>

        {/* Stats */}
        <section className="relative border-b border-line">
          <div className="container-x">
            <div className="grid grid-cols-2 md:grid-cols-4">
              {STATS.map((s) => (
                <div key={s.label} className="py-10 pr-6 border-r border-line last:border-r-0">
                  <div className="font-head font-black text-4xl sm:text-5xl text-white">{s.value}</div>
                  <div className="mono-label text-text-2 mt-2">{s.label}</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Values */}
        <section data-testid="about-values" className="relative py-24">
          <div className="container-x">
            <div className="max-w-2xl mb-14">
              <span className="mono-label text-accent">[ What we stand for ]</span>
              <h2 className="font-head font-black tracking-tighter text-4xl sm:text-5xl mt-4 text-balance">
                Values that shape the work.
              </h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-line border border-line">
              {VALUES.map((v, i) => (
                <motion.div
                  key={v.title}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: i * 0.08 }}
                  className="group bg-surface p-8 lg:p-10 hover:bg-surface-2 transition-colors"
                >
                  <div className="w-12 h-12 flex items-center justify-center border border-line group-hover:border-accent group-hover:text-accent transition-colors">
                    <v.icon className="w-5 h-5" />
                  </div>
                  <h3 className="font-head font-bold text-xl sm:text-2xl mt-6 mb-2">{v.title}</h3>
                  <p className="text-text-2 leading-relaxed max-w-md">{v.text}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="relative py-24 border-t border-line">
          <div className="container-x text-center">
            <h2 className="font-head font-black tracking-tighter text-4xl sm:text-5xl lg:text-6xl text-balance max-w-3xl mx-auto">
              Let's build something Sacramento talks about.
            </h2>
            <a
              href="/#contact"
              data-testid="about-cta"
              className="inline-flex items-center gap-2 bg-accent text-ink font-head font-bold px-8 py-4 mt-10 hover:bg-white transition-colors group"
            >
              Start your project
              <ArrowUpRight className="w-5 h-5 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
            </a>
          </div>
        </section>
      </main>

      <AnimatePresence>
        {lightbox && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            data-testid="gallery-lightbox"
            className="fixed inset-0 z-[100] bg-ink/95 backdrop-blur-md flex items-center justify-center p-4 sm:p-8"
            onClick={() => setLightbox(null)}
          >
            <button
              onClick={() => setLightbox(null)}
              data-testid="gallery-lightbox-close"
              className="absolute top-5 right-5 w-11 h-11 flex items-center justify-center border border-line text-white hover:border-accent hover:text-accent transition-colors"
              aria-label="Close"
            >
              <X className="w-5 h-5" />
            </button>
            <motion.div
              initial={{ scale: 0.96, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.96, opacity: 0 }}
              transition={{ duration: 0.25 }}
              className="flex flex-col items-center"
              onClick={(e) => e.stopPropagation()}
            >
              <img
                src={lightbox.src}
                alt={lightbox.caption}
                className="max-h-[82vh] max-w-full object-contain border border-line"
              />
              <p className="mono-label text-text-2 mt-4 text-center">{lightbox.caption}</p>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <Footer />
    </div>
  );
}
